import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Common
    common: {
      search: 'Search',
      filter: 'Filter',
      actions: 'Actions',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      add: 'Add',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      continue: 'Continue',
      submit: 'Submit',
      loading: 'Loading...',
      noData: 'No data available',
      all: 'All',
      status: 'Status',
      date: 'Date',
      total: 'Total',
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      approved: 'Approved',
      completed: 'Completed',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      and: 'and',
    },
    
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      vehicles: 'Vehicles',
      drivers: 'Drivers',
      lessees: 'Lessees',
      fleetManagers: 'Fleet Managers',
      workshops: 'Workshops',
      workshopJobs: 'My Jobs',
      workshopAppointments: 'Appointments',
      workshopCustomers: 'Customers',
      workshopPartners: 'Leasing Partners',
      workshopSettings: 'Workshop Settings',
      chat: 'Chat',
      liveFleet: 'Live Fleet',
      parkingMap: 'Parking Map',
      maintenance: 'Maintenance',
      fuelLogs: 'Fuel Logs',
      inspections: 'Inspections',
      photoCompare: 'Photo Compare',
      damageReports: 'Damage Reports',
      history: 'History',
      analytics: 'Analytics',
      optimization: 'Optimization',
      capacityAnalysis: 'Capacity Analysis',
      billing: 'Billing',
      userManagement: 'User Management',
      settings: 'Settings',
      logout: 'Logout',
      dailyCheckIn: 'Daily Check-In',
      companies: 'Companies',
      allUsers: 'All Users',
      overview: 'Overview',
    },
    
    // Landing Page
    landing: {
      heroTitle: 'Manage Your Fleet',
      heroTitleHighlight: 'Smarter & Easier',
      heroSubtitle: 'The all-in-one platform for vehicle tracking, driver management, maintenance scheduling, and AI-powered inspections.',
      startFreeTrial: 'Start 14-Day Free Trial',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      noCreditCard: 'No credit card required. 14-day free trial.',
      features: 'Features',
      pricing: 'Pricing',
      
      // Features
      featuresTitle: 'Everything You Need to Manage Your Fleet',
      featuresSubtitle: 'Powerful features designed to streamline your fleet operations and reduce costs.',
      vehicleManagement: 'Vehicle Management',
      vehicleManagementDesc: 'Track your entire fleet in one place. Monitor status, mileage, and maintenance schedules.',
      driverManagement: 'Driver Management',
      driverManagementDesc: 'Manage your drivers, track their activity, and ensure compliance with daily check-ins.',
      photoInspections: 'Photo Inspections',
      photoInspectionsDesc: 'Capture vehicle conditions with photos. AI-powered damage detection alerts you instantly.',
      maintenanceScheduling: 'Maintenance Scheduling',
      maintenanceSchedulingDesc: 'Never miss a service. Schedule maintenance by date or mileage with automatic reminders.',
      analyticsReports: 'Analytics & Reports',
      analyticsReportsDesc: 'Gain insights with detailed analytics. Track KPIs and export reports with ease.',
      roleBasedAccess: 'Role-Based Access',
      roleBasedAccessDesc: 'Control who sees what. Admins, fleet managers, and drivers each get tailored access.',
      
      // Pricing
      pricingTitle: 'Simple, Transparent Pricing',
      pricingSubtitle: 'Choose the plan that fits your fleet size. All plans include a 14-day free trial.',
      monthly: 'Monthly',
      yearly: 'Yearly',
      save20: 'Save 20%',
      month: 'month',
      year: 'year',
      mostPopular: 'Most Popular',
      contactSales: 'Contact Sales',
      custom: 'Custom',
      contactForPricing: 'Contact us for pricing',
      billedYearly: 'Billed',
      
      // Plans
      basic: 'Basic',
      basicDesc: 'For small fleets',
      professional: 'Professional',
      professionalDesc: 'For growing fleets',
      enterprise: 'Enterprise',
      enterpriseDesc: 'For large fleets',
      custom: 'Custom',
      customDesc: 'For unlimited needs',
      
      // Plan Features
      upToVehicles: 'Up to {count} vehicles',
      unlimitedVehicles: 'Unlimited vehicles',
      unlimitedDrivers: 'Unlimited drivers',
      perVehicleMonth: '€{price}/vehicle/month',
      allFeatures: 'All features included',
      dailyCheckIns: 'Daily check-ins',
      maintenanceSchedulingFeature: 'Maintenance scheduling',
      photoComparison: 'Photo comparison',
      fuelTracking: 'Fuel tracking',
      aiDamageDetection: 'AI damage detection',
      analyticsReportsFeature: 'Analytics & reports',
      csvExports: 'CSV exports',
      emailSupport: 'Email support',
      prioritySupport: 'Priority support',
      phoneSupport: 'Phone support',
      dedicatedManager: 'Dedicated account manager',
      customIntegrations: 'Custom integrations',
      slaGuarantee: 'SLA guarantee',
      onPremise: 'On-premise option',
      
      // CTA
      ctaTitle: 'Ready to Transform Your Fleet Management?',
      ctaSubtitle: 'Join hundreds of companies already using Cofleet to streamline their operations.',
      startYourFreeTrial: 'Start Your Free Trial',
      
      // Footer
      allRightsReserved: 'All rights reserved.',
    },
    
    // Auth
    auth: {
      login: 'Login',
      register: 'Register',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot Password?',
      dontHaveAccount: "Don't have an account?",
      alreadyHaveAccount: 'Already have an account?',
      createAccount: 'Create your account',
      companyName: 'Company Name',
      yourName: 'Your Name',
      startWithCompany: 'Start with your company information',
      yourDetails: 'Your details',
      youllBeAdmin: "You'll be the admin for",
      choosePlan: 'Choose your plan',
      selectPlan: 'Select Plan',
      billingCycle: 'Billing Cycle',
      trialPeriod: 'Trial Period',
      daysFree: '14 days free',
      termsAgreement: 'By creating an account, you agree to our Terms of Service and Privacy Policy.',
      welcomeTo: 'Welcome to Cofleet',
      welcomeDesc: 'Join hundreds of companies managing their fleets smarter with AI-powered inspections, maintenance scheduling, and real-time analytics.',
      companiesCount: 'Companies',
      vehiclesManaged: 'Vehicles Managed',
      uptime: 'Uptime',
      support: 'Support',
    },
    
    // Dashboard
    dashboard: {
      welcome: 'Welcome',
      welcomeBack: 'Welcome back',
      fleetOverview: 'Fleet Overview',
      totalVehicles: 'Total Vehicles',
      activeDrivers: 'Active Drivers',
      pendingMaintenance: 'Pending Maintenance',
      todayInspections: "Today's Inspections",
      recentActivity: 'Recent Activity',
      quickActions: 'Quick Actions',
      viewAll: 'View All',
      lastVehicleUsed: 'Last Vehicle Used',
      totalKmDriven: 'Total KM Driven',
      checkInsThisWeek: 'Check-ins this week',
      checkInsThisMonth: 'Check-ins this month',
      currentStreak: 'Day Streak!',
      keepUpGreatWork: 'Keep up the great work!',
      thisWeek: 'This Week',
      last7Days: 'Last 7 days',
    },
    
    // Vehicles
    vehicles: {
      title: 'Vehicles',
      addVehicle: 'Add Vehicle',
      editVehicle: 'Edit Vehicle',
      vehicleDetails: 'Vehicle Details',
      licensePlate: 'License Plate',
      make: 'Make',
      model: 'Model',
      year: 'Year',
      mileage: 'Mileage',
      currentMileage: 'Current Mileage',
      fuelType: 'Fuel Type',
      color: 'Color',
      vin: 'VIN',
      available: 'Available',
      inUse: 'In Use',
      underMaintenance: 'Under Maintenance',
      outOfService: 'Out of Service',
      assistancePhone: 'Assistance Phone',
      searchPlaceholder: 'Search by plate, make, model...',
      totalEntries: 'total vehicles',
      viewHistory: 'View History',
      noVehicles: 'No vehicles found',
      registrationDate: 'First Registration Date',
      tyreNumber: 'Tyre Number',
      insuranceExpiry: 'Insurance Expiry',
      insuranceExpiryHelp: 'Vehicle insurance expiration date',
      expired: 'Expired',
    },
    
    // Drivers
    drivers: {
      title: 'Drivers',
      addDriver: 'Add Driver',
      editDriver: 'Edit Driver',
      driverDetails: 'Driver Details',
      name: 'Name',
      phone: 'Phone',
      licenseNumber: 'License Number',
      licenseExpiry: 'License Expiry',
      assignedVehicle: 'Assigned Vehicle',
      onTrip: 'On Trip',
      offDuty: 'Off Duty',
      searchPlaceholder: 'Search by name, email, or license number...',
      totalEntries: 'total drivers',
      contact: 'Contact',
      license: 'License',
      expiry: 'Expiry',
      expired: 'Expired',
      viewHistory: 'View History',
      noDrivers: 'No drivers found',
    },
    
    // Maintenance
    maintenance: {
      title: 'Maintenance',
      scheduleMaintenance: 'Schedule Maintenance',
      editMaintenance: 'Edit Maintenance Record',
      maintenanceType: 'Maintenance Type',
      scheduledDate: 'Scheduled Date',
      scheduledDistance: 'Scheduled Distance (km)',
      completedDate: 'Completed Date',
      cost: 'Cost',
      notes: 'Notes',
      description: 'Description',
      scheduled: 'Scheduled',
      inProgress: 'In Progress',
      overdue: 'Overdue',
      searchPlaceholder: 'Search by description, type, or vehicle...',
      totalEntries: 'total records',
      vehicle: 'Vehicle',
      type: 'Type',
      oilChange: 'Oil Change',
      tireRotation: 'Tire Rotation',
      brakeInspection: 'Brake Inspection',
      engineTuneUp: 'Engine Tune-up',
      transmission: 'Transmission Service',
      other: 'Other',
      markComplete: 'Mark Complete',
      noMaintenance: 'No maintenance records found',
    },
    
    // Fuel Logs
    fuelLogs: {
      title: 'Fuel Logs',
      addFuelLog: 'Add Fuel Log',
      editFuelLog: 'Edit Fuel Log',
      quantity: 'Quantity (L)',
      pricePerLiter: 'Price/L',
      totalCost: 'Total Cost',
      station: 'Station',
      odometer: 'Odometer',
      searchPlaceholder: 'Search by vehicle or station...',
      totalEntries: 'total entries',
      driver: 'Driver',
      allVehicles: 'All Vehicles',
      noFuelLogs: 'No fuel logs found',
    },
    
    // Inspections
    inspections: {
      title: 'Daily Inspections',
      newInspection: 'New Inspection',
      inspectionDetails: 'Inspection Details',
      odometerReading: 'Odometer Reading',
      photos: 'Photos',
      aiAnalysis: 'AI Analysis',
      flagged: 'Flagged',
      searchPlaceholder: 'Search by driver or vehicle...',
      totalEntries: 'total inspections',
      allStatus: 'All Status',
      noInspections: 'No inspections found',
      viewPhotos: 'View Photos',
      comparePhotos: 'Compare Photos',
    },
    
    // Photo Compare
    photoCompare: {
      title: 'Photo Comparison',
      subtitle: 'Compare inspection photos to detect changes and damages',
      selectInspections: 'Select Two Inspections to Compare',
      selectFirst: 'Select First Inspection',
      selectSecond: 'Select Second Inspection',
      compare: 'Compare',
      analyzing: 'Analyzing...',
      aiAnalysisResult: 'AI Analysis Result',
      beforePhoto: 'Before',
      afterPhoto: 'After',
      damagesDetected: 'Damages Detected',
      noDamages: 'No damages detected',
      changesFound: 'Changes Found',
      noChanges: 'No significant changes detected',
    },
    
    // Damage Reports
    damageReports: {
      title: 'Damage Reports',
      newReport: 'Report Damage',
      reportDetails: 'Report Details',
      reportDamage: 'Report Damage/Scratch',
      selectVehicle: 'Select Vehicle',
      describeTheDamage: 'Describe the Damage',
      describePlaceholder: 'Describe the damage or scratch location and severity...',
      uploadMedia: 'Upload Photo or Video (optional)',
      maxVideoLength: 'Max 10-15 second video or photo',
      takePhoto: 'Take Photo',
      recordVideo: 'Record Video',
      submitReport: 'Submit Report',
      reviewed: 'Reviewed',
      resolved: 'Resolved',
      markReviewed: 'Mark Reviewed',
      markResolved: 'Mark Resolved',
      searchPlaceholder: 'Search damage reports...',
      totalEntries: 'total reports',
      noReports: 'No damage reports found',
      filterByStatus: 'Filter by Status',
      filterByVehicle: 'Filter by Vehicle',
      dateReported: 'Date Reported',
      media: 'Media',
    },
    
    // History
    history: {
      title: 'Fleet History',
      subtitle: 'Track vehicle and driver usage over time',
      period: 'Period',
      last7Days: 'Last 7 days',
      last14Days: 'Last 14 days',
      last30Days: 'Last 30 days',
      mostActiveVehicles: 'Most Active Vehicles',
      mostActiveDrivers: 'Most Active Drivers',
      dailyActivity: 'Daily Activity',
      checkIns: 'Check-ins',
      assignments: 'Assignments',
      noActivity: 'No activity in this period',
    },
    
    // Analytics
    analytics: {
      title: 'Analytics',
      vehicleAnalytics: 'Vehicle Analytics',
      driverAnalytics: 'Driver Analytics',
      totalKmDriven: 'Total KM Driven',
      avgKmPerVehicle: 'Avg KM/Vehicle',
      avgKmPerCheckIn: 'Avg KM/Check-in',
      predictedKmYearEnd: 'Predicted KM (Year End)',
      kmDrivenByVehicle: 'KM Driven by Vehicle',
      predictedKm: 'Predicted KM',
      vehicleDetails: 'Vehicle Details',
      driverDetails: 'Driver Details',
      exportCsv: 'Export CSV',
      filterByVehicle: 'Filter by Vehicle',
      allVehicles: 'All Vehicles',
      clearFilter: 'Clear Filter',
      checkInCount: 'Check-in Count',
      lastCheckIn: 'Last Check-in',
      avgDaily: 'Avg Daily',
      kmDrivenByDriver: 'KM Driven by Driver',
      complianceRate: 'Check-in Compliance Rate',
      weeklyPattern: 'Weekly Activity Pattern (Fleet Average)',
      vehiclesUsed: 'Vehicles Used',
      activeDays: 'Active Days',
      compliance: 'Compliance',
    },
    
    // User Management
    users: {
      title: 'User Management',
      addUser: 'Add User',
      editUser: 'Edit User',
      role: 'Role',
      admin: 'Admin',
      fleetManager: 'Fleet Manager',
      workshop: 'Workshop',
      driver: 'Driver',
      superAdmin: 'Super Admin',
      created: 'Created',
      searchPlaceholder: 'Search users...',
      totalEntries: 'total users',
      noUsers: 'No users found',
      resetPassword: 'Reset Password',
      newPassword: 'New Password',
      passwordResetSuccess: 'Password reset successfully',
    },
    
    // Super Admin
    superAdmin: {
      title: 'Super Admin',
      platformOverview: 'Platform Overview',
      companyManagement: 'Company Management',
      allUsers: 'All Users',
      totalCompanies: 'Total Companies',
      activeCompanies: 'Active Companies',
      totalUsers: 'Total Users',
      totalVehicles: 'Total Vehicles',
      totalDrivers: 'Total Drivers',
      companiesByPlan: 'Companies by Plan',
      recentSignups: 'Recent Signups',
      quickActions: 'Quick Actions',
      viewCompanies: 'View Companies',
      manageUsers: 'Manage Users',
      platformAnalytics: 'Platform Analytics',
      billingOverview: 'Billing Overview',
      plan: 'Plan',
      trialEnds: 'Trial Ends',
      users: 'Users',
      deactivate: 'Deactivate',
      activate: 'Activate',
      extendTrial: 'Extend Trial',
      deleteCompany: 'Delete Company',
      deleteConfirm: 'Are you sure you want to delete this company? This will delete ALL data including users, vehicles, and records. This action cannot be undone.',
      loginAs: 'Login As',
      impersonate: 'Impersonate User',
      viewDetails: 'View Details',
      editCompany: 'Edit Company',
      trialEndDate: 'Trial End Date',
      company: 'Company',
      searchCompanies: 'Search companies...',
      searchUsers: 'Search by name, email, or company...',
      allPlans: 'All Plans',
      allStatus: 'All Status',
      allRoles: 'All Roles',
    },
    
    // Settings
    settings: {
      title: 'Settings',
      profile: 'Profile',
      account: 'Account',
      notifications: 'Notifications',
      language: 'Language',
      theme: 'Theme',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      saveChanges: 'Save Changes',
    },
    
    // Check-in
    checkIn: {
      title: 'Daily Check-In',
      selectVehicle: 'Select Vehicle',
      enterOdometer: 'Enter Odometer Reading',
      takePhotos: 'Take Photos',
      exteriorFront: 'Exterior Front',
      exteriorBack: 'Exterior Back',
      exteriorLeft: 'Exterior Left',
      exteriorRight: 'Exterior Right',
      interiorFront: 'Interior Front',
      interiorBack: 'Interior Back',
      submitCheckIn: 'Submit Check-In',
      photosRequired: '6 photos required',
      photosTaken: 'photos taken',
      checkInSuccess: 'Check-in submitted successfully!',
      callAssistance: 'Call Assistance',
      reportDamage: 'Report Damage',
    },
    
    // Messages
    messages: {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Info',
      saved: 'Saved successfully',
      deleted: 'Deleted successfully',
      updated: 'Updated successfully',
      created: 'Created successfully',
      loginSuccess: 'Login successful',
      loginError: 'Invalid credentials',
      logoutSuccess: 'Logged out successfully',
      sessionExpired: 'Session expired. Please login again.',
      networkError: 'Network error. Please try again.',
      serverError: 'Server error. Please try again later.',
      confirmDelete: 'Are you sure you want to delete this?',
      noPermission: 'You do not have permission to perform this action.',
    },
  },
  
  el: {
    // Common
    common: {
      search: 'Αναζήτηση',
      filter: 'Φίλτρο',
      actions: 'Ενέργειες',
      save: 'Αποθήκευση',
      cancel: 'Ακύρωση',
      delete: 'Διαγραφή',
      edit: 'Επεξεργασία',
      view: 'Προβολή',
      add: 'Προσθήκη',
      close: 'Κλείσιμο',
      back: 'Πίσω',
      next: 'Επόμενο',
      continue: 'Συνέχεια',
      submit: 'Υποβολή',
      loading: 'Φόρτωση...',
      noData: 'Δεν υπάρχουν δεδομένα',
      all: 'Όλα',
      status: 'Κατάσταση',
      date: 'Ημερομηνία',
      total: 'Σύνολο',
      active: 'Ενεργό',
      inactive: 'Ανενεργό',
      pending: 'Εκκρεμεί',
      approved: 'Εγκρίθηκε',
      completed: 'Ολοκληρώθηκε',
      yes: 'Ναι',
      no: 'Όχι',
      or: 'ή',
      and: 'και',
    },
    
    // Navigation
    nav: {
      dashboard: 'Πίνακας Ελέγχου',
      vehicles: 'Οχήματα',
      drivers: 'Οδηγοί',
      lessees: 'Μισθωτές',
      fleetManagers: 'Υπεύθυνοι Στόλου',
      workshops: 'Συνεργεία',
      workshopJobs: 'Εργασίες',
      workshopAppointments: 'Ραντεβού',
      workshopCustomers: 'Πελάτες',
      workshopPartners: 'Συνεργάτες Leasing',
      workshopSettings: 'Ρυθμίσεις συνεργείου',
      chat: 'Chat',
      liveFleet: 'Live Fleet',
      parkingMap: 'Χάρτης Στάθμευσης',
      maintenance: 'Συντήρηση',
      fuelLogs: 'Καύσιμα',
      inspections: 'Επιθεωρήσεις',
      photoCompare: 'Σύγκριση Φωτό',
      damageReports: 'Αναφορές Ζημιών',
      history: 'Ιστορικό',
      analytics: 'Στατιστικά',
      optimization: 'Βελτιστοποίηση',
      capacityAnalysis: 'Ανάλυση Χωρητικότητας',
      billing: 'Χρεώσεις',
      userManagement: 'Διαχείριση Χρηστών',
      settings: 'Ρυθμίσεις',
      logout: 'Αποσύνδεση',
      dailyCheckIn: 'Ημερήσιος Έλεγχος',
      companies: 'Εταιρείες',
      allUsers: 'Όλοι οι Χρήστες',
      overview: 'Επισκόπηση',
    },
    
    // Landing Page
    landing: {
      heroTitle: 'Διαχειριστείτε τον Στόλο σας',
      heroTitleHighlight: 'Πιο Έξυπνα & Εύκολα',
      heroSubtitle: 'Η ολοκληρωμένη πλατφόρμα για παρακολούθηση οχημάτων, διαχείριση οδηγών, προγραμματισμό συντήρησης και επιθεωρήσεις με τεχνητή νοημοσύνη.',
      startFreeTrial: 'Ξεκινήστε 14 Ημέρες Δωρεάν',
      signIn: 'Σύνδεση',
      signUp: 'Εγγραφή',
      noCreditCard: 'Δεν απαιτείται πιστωτική κάρτα. 14 ημέρες δωρεάν δοκιμή.',
      features: 'Χαρακτηριστικά',
      pricing: 'Τιμές',
      
      // Features
      featuresTitle: 'Όλα όσα Χρειάζεστε για τη Διαχείριση του Στόλου σας',
      featuresSubtitle: 'Ισχυρά χαρακτηριστικά σχεδιασμένα για να βελτιστοποιήσουν τις λειτουργίες του στόλου σας και να μειώσουν τα κόστη.',
      vehicleManagement: 'Διαχείριση Οχημάτων',
      vehicleManagementDesc: 'Παρακολουθήστε ολόκληρο τον στόλο σας σε ένα μέρος. Ελέγξτε κατάσταση, χιλιόμετρα και προγράμματα συντήρησης.',
      driverManagement: 'Διαχείριση Οδηγών',
      driverManagementDesc: 'Διαχειριστείτε τους οδηγούς σας, παρακολουθήστε τη δραστηριότητά τους και διασφαλίστε τη συμμόρφωση με τους ημερήσιους ελέγχους.',
      photoInspections: 'Φωτογραφικές Επιθεωρήσεις',
      photoInspectionsDesc: 'Καταγράψτε την κατάσταση των οχημάτων με φωτογραφίες. Η ανίχνευση ζημιών με AI σας ειδοποιεί άμεσα.',
      maintenanceScheduling: 'Προγραμματισμός Συντήρησης',
      maintenanceSchedulingDesc: 'Μην χάσετε ποτέ ένα σέρβις. Προγραμματίστε συντήρηση με βάση την ημερομηνία ή τα χιλιόμετρα με αυτόματες υπενθυμίσεις.',
      analyticsReports: 'Στατιστικά & Αναφορές',
      analyticsReportsDesc: 'Αποκτήστε πληροφορίες με λεπτομερή στατιστικά. Παρακολουθήστε KPIs και εξάγετε αναφορές εύκολα.',
      roleBasedAccess: 'Πρόσβαση βάσει Ρόλου',
      roleBasedAccessDesc: 'Ελέγξτε ποιος βλέπει τι. Διαχειριστές, διευθυντές στόλου και οδηγοί έχουν προσαρμοσμένη πρόσβαση.',
      
      // Pricing
      pricingTitle: 'Απλές, Διαφανείς Τιμές',
      pricingSubtitle: 'Επιλέξτε το πλάνο που ταιριάζει στο μέγεθος του στόλου σας. Όλα τα πλάνα περιλαμβάνουν 14 ημέρες δωρεάν δοκιμή.',
      monthly: 'Μηνιαία',
      yearly: 'Ετήσια',
      save20: 'Εξοικονόμηση 20%',
      month: 'μήνα',
      year: 'έτος',
      mostPopular: 'Πιο Δημοφιλές',
      contactSales: 'Επικοινωνία με Πωλήσεις',
      custom: 'Προσαρμοσμένο',
      contactForPricing: 'Επικοινωνήστε μαζί μας για τιμές',
      billedYearly: 'Χρέωση',
      
      // Plans
      basic: 'Βασικό',
      basicDesc: 'Για μικρούς στόλους',
      professional: 'Επαγγελματικό',
      professionalDesc: 'Για αναπτυσσόμενους στόλους',
      enterprise: 'Εταιρικό',
      enterpriseDesc: 'Για μεγάλους στόλους',
      custom: 'Προσαρμοσμένο',
      customDesc: 'Για απεριόριστες ανάγκες',
      
      // Plan Features
      upToVehicles: 'Έως {count} οχήματα',
      unlimitedVehicles: 'Απεριόριστα οχήματα',
      unlimitedDrivers: 'Απεριόριστοι οδηγοί',
      perVehicleMonth: '€{price}/όχημα/μήνα',
      allFeatures: 'Όλες οι λειτουργίες',
      dailyCheckIns: 'Ημερήσιοι έλεγχοι',
      maintenanceSchedulingFeature: 'Προγραμματισμός συντήρησης',
      photoComparison: 'Σύγκριση φωτογραφιών',
      fuelTracking: 'Παρακολούθηση καυσίμων',
      aiDamageDetection: 'Ανίχνευση ζημιών με AI',
      analyticsReportsFeature: 'Στατιστικά & αναφορές',
      csvExports: 'Εξαγωγές CSV',
      emailSupport: 'Υποστήριξη μέσω email',
      prioritySupport: 'Υποστήριξη προτεραιότητας',
      phoneSupport: 'Τηλεφωνική υποστήριξη',
      dedicatedManager: 'Αποκλειστικός υπεύθυνος λογαριασμού',
      customIntegrations: 'Προσαρμοσμένες ενσωματώσεις',
      slaGuarantee: 'Εγγύηση SLA',
      onPremise: 'Επιλογή on-premise',
      
      // CTA
      ctaTitle: 'Έτοιμοι να Μεταμορφώσετε τη Διαχείριση του Στόλου σας;',
      ctaSubtitle: 'Γίνετε μέλος εκατοντάδων εταιρειών που ήδη χρησιμοποιούν το Cofleet για να βελτιστοποιήσουν τις λειτουργίες τους.',
      startYourFreeTrial: 'Ξεκινήστε τη Δωρεάν Δοκιμή σας',
      
      // Footer
      allRightsReserved: 'Με επιφύλαξη παντός δικαιώματος.',
    },
    
    // Auth
    auth: {
      login: 'Σύνδεση',
      register: 'Εγγραφή',
      email: 'Email',
      password: 'Κωδικός',
      confirmPassword: 'Επιβεβαίωση Κωδικού',
      forgotPassword: 'Ξεχάσατε τον κωδικό;',
      dontHaveAccount: 'Δεν έχετε λογαριασμό;',
      alreadyHaveAccount: 'Έχετε ήδη λογαριασμό;',
      createAccount: 'Δημιουργήστε τον λογαριασμό σας',
      companyName: 'Όνομα Εταιρείας',
      yourName: 'Το Όνομά σας',
      startWithCompany: 'Ξεκινήστε με τα στοιχεία της εταιρείας σας',
      yourDetails: 'Τα στοιχεία σας',
      youllBeAdmin: 'Θα είστε ο διαχειριστής της',
      choosePlan: 'Επιλέξτε το πλάνο σας',
      selectPlan: 'Επιλέξτε Πλάνο',
      billingCycle: 'Κύκλος Χρέωσης',
      trialPeriod: 'Περίοδος Δοκιμής',
      daysFree: '14 ημέρες δωρεάν',
      termsAgreement: 'Δημιουργώντας λογαριασμό, συμφωνείτε με τους Όρους Υπηρεσίας και την Πολιτική Απορρήτου μας.',
      welcomeTo: 'Καλώς ήρθατε στο Cofleet',
      welcomeDesc: 'Γίνετε μέλος εκατοντάδων εταιρειών που διαχειρίζονται τους στόλους τους πιο έξυπνα με επιθεωρήσεις AI, προγραμματισμό συντήρησης και αναλυτικά σε πραγματικό χρόνο.',
      companiesCount: 'Εταιρείες',
      vehiclesManaged: 'Οχήματα υπό Διαχείριση',
      uptime: 'Διαθεσιμότητα',
      support: 'Υποστήριξη',
    },
    
    // Dashboard
    dashboard: {
      welcome: 'Καλώς ήρθατε',
      welcomeBack: 'Καλώς ήρθατε ξανά',
      fleetOverview: 'Επισκόπηση Στόλου',
      totalVehicles: 'Συνολικά Οχήματα',
      activeDrivers: 'Ενεργοί Οδηγοί',
      pendingMaintenance: 'Εκκρεμής Συντήρηση',
      todayInspections: 'Σημερινές Επιθεωρήσεις',
      recentActivity: 'Πρόσφατη Δραστηριότητα',
      quickActions: 'Γρήγορες Ενέργειες',
      viewAll: 'Προβολή Όλων',
      lastVehicleUsed: 'Τελευταίο Όχημα',
      totalKmDriven: 'Συνολικά ΧΛΜ',
      checkInsThisWeek: 'Έλεγχοι αυτή την εβδομάδα',
      checkInsThisMonth: 'Έλεγχοι αυτόν τον μήνα',
      currentStreak: 'Ημέρες Συνεχόμενα!',
      keepUpGreatWork: 'Συνεχίστε την καλή δουλειά!',
      thisWeek: 'Αυτή την Εβδομάδα',
      last7Days: 'Τελευταίες 7 ημέρες',
    },
    
    // Vehicles
    vehicles: {
      title: 'Οχήματα',
      addVehicle: 'Προσθήκη Οχήματος',
      editVehicle: 'Επεξεργασία Οχήματος',
      vehicleDetails: 'Στοιχεία Οχήματος',
      licensePlate: 'Πινακίδα',
      make: 'Μάρκα',
      model: 'Μοντέλο',
      year: 'Έτος',
      mileage: 'Χιλιόμετρα',
      currentMileage: 'Τρέχοντα Χιλιόμετρα',
      fuelType: 'Τύπος Καυσίμου',
      color: 'Χρώμα',
      vin: 'VIN',
      available: 'Διαθέσιμο',
      inUse: 'Σε Χρήση',
      underMaintenance: 'Υπό Συντήρηση',
      outOfService: 'Εκτός Λειτουργίας',
      assistancePhone: 'Τηλέφωνο Βοήθειας',
      searchPlaceholder: 'Αναζήτηση με πινακίδα, μάρκα, μοντέλο...',
      totalEntries: 'συνολικά οχήματα',
      viewHistory: 'Προβολή Ιστορικού',
      noVehicles: 'Δεν βρέθηκαν οχήματα',
      registrationDate: 'Ημερομηνία Πρώτης Άδειας',
      tyreNumber: 'Αριθμός Ελαστικών',
      insuranceExpiry: 'Λήξη Ασφάλειας',
      insuranceExpiryHelp: 'Ημερομηνία λήξης ασφάλειας οχήματος',
      expired: 'Έληξε',
    },
    
    // Drivers
    drivers: {
      title: 'Οδηγοί',
      addDriver: 'Προσθήκη Οδηγού',
      editDriver: 'Επεξεργασία Οδηγού',
      driverDetails: 'Στοιχεία Οδηγού',
      name: 'Όνομα',
      phone: 'Τηλέφωνο',
      licenseNumber: 'Αριθμός Διπλώματος',
      licenseExpiry: 'Λήξη Διπλώματος',
      assignedVehicle: 'Ανατεθειμένο Όχημα',
      onTrip: 'Σε Διαδρομή',
      offDuty: 'Εκτός Υπηρεσίας',
      searchPlaceholder: 'Αναζήτηση με όνομα, email ή αριθμό διπλώματος...',
      totalEntries: 'συνολικά οδηγοί',
      contact: 'Επικοινωνία',
      license: 'Δίπλωμα',
      expiry: 'Λήξη',
      expired: 'Έληξε',
      viewHistory: 'Προβολή Ιστορικού',
      noDrivers: 'Δεν βρέθηκαν οδηγοί',
    },
    
    // Maintenance
    maintenance: {
      title: 'Συντήρηση',
      scheduleMaintenance: 'Προγραμματισμός Συντήρησης',
      editMaintenance: 'Επεξεργασία Εγγραφής Συντήρησης',
      maintenanceType: 'Τύπος Συντήρησης',
      scheduledDate: 'Προγραμματισμένη Ημερομηνία',
      scheduledDistance: 'Προγραμματισμένη Απόσταση (χλμ)',
      completedDate: 'Ημερομηνία Ολοκλήρωσης',
      cost: 'Κόστος',
      notes: 'Σημειώσεις',
      description: 'Περιγραφή',
      scheduled: 'Προγραμματισμένο',
      inProgress: 'Σε Εξέλιξη',
      overdue: 'Εκπρόθεσμο',
      searchPlaceholder: 'Αναζήτηση με περιγραφή, τύπο ή όχημα...',
      totalEntries: 'συνολικές εγγραφές',
      vehicle: 'Όχημα',
      type: 'Τύπος',
      oilChange: 'Αλλαγή Λαδιού',
      tireRotation: 'Εναλλαγή Ελαστικών',
      brakeInspection: 'Έλεγχος Φρένων',
      engineTuneUp: 'Ρύθμιση Κινητήρα',
      transmission: 'Σέρβις Κιβωτίου',
      other: 'Άλλο',
      markComplete: 'Σήμανση Ολοκλήρωσης',
      noMaintenance: 'Δεν βρέθηκαν εγγραφές συντήρησης',
    },
    
    // Fuel Logs
    fuelLogs: {
      title: 'Καύσιμα',
      addFuelLog: 'Προσθήκη Καυσίμου',
      editFuelLog: 'Επεξεργασία Καυσίμου',
      quantity: 'Ποσότητα (L)',
      pricePerLiter: 'Τιμή/L',
      totalCost: 'Συνολικό Κόστος',
      station: 'Πρατήριο',
      odometer: 'Χιλιομετρητής',
      searchPlaceholder: 'Αναζήτηση με όχημα ή πρατήριο...',
      totalEntries: 'συνολικές εγγραφές',
      driver: 'Οδηγός',
      allVehicles: 'Όλα τα Οχήματα',
      noFuelLogs: 'Δεν βρέθηκαν εγγραφές καυσίμων',
    },
    
    // Inspections
    inspections: {
      title: 'Ημερήσιες Επιθεωρήσεις',
      newInspection: 'Νέα Επιθεώρηση',
      inspectionDetails: 'Στοιχεία Επιθεώρησης',
      odometerReading: 'Ένδειξη Χιλιομετρητή',
      photos: 'Φωτογραφίες',
      aiAnalysis: 'Ανάλυση AI',
      flagged: 'Επισημασμένο',
      searchPlaceholder: 'Αναζήτηση με οδηγό ή όχημα...',
      totalEntries: 'συνολικές επιθεωρήσεις',
      allStatus: 'Όλες οι Καταστάσεις',
      noInspections: 'Δεν βρέθηκαν επιθεωρήσεις',
      viewPhotos: 'Προβολή Φωτογραφιών',
      comparePhotos: 'Σύγκριση Φωτογραφιών',
    },
    
    // Photo Compare
    photoCompare: {
      title: 'Σύγκριση Φωτογραφιών',
      subtitle: 'Συγκρίνετε φωτογραφίες επιθεωρήσεων για να εντοπίσετε αλλαγές και ζημιές',
      selectInspections: 'Επιλέξτε Δύο Επιθεωρήσεις για Σύγκριση',
      selectFirst: 'Επιλέξτε Πρώτη Επιθεώρηση',
      selectSecond: 'Επιλέξτε Δεύτερη Επιθεώρηση',
      compare: 'Σύγκριση',
      analyzing: 'Ανάλυση...',
      aiAnalysisResult: 'Αποτέλεσμα Ανάλυσης AI',
      beforePhoto: 'Πριν',
      afterPhoto: 'Μετά',
      damagesDetected: 'Εντοπίστηκαν Ζημιές',
      noDamages: 'Δεν εντοπίστηκαν ζημιές',
      changesFound: 'Βρέθηκαν Αλλαγές',
      noChanges: 'Δεν εντοπίστηκαν σημαντικές αλλαγές',
    },
    
    // Damage Reports
    damageReports: {
      title: 'Αναφορές Ζημιών',
      newReport: 'Αναφορά Ζημιάς',
      reportDetails: 'Στοιχεία Αναφοράς',
      reportDamage: 'Αναφορά Ζημιάς/Γρατζουνιάς',
      selectVehicle: 'Επιλέξτε Όχημα',
      describeTheDamage: 'Περιγράψτε τη Ζημιά',
      describePlaceholder: 'Περιγράψτε τη θέση και τη σοβαρότητα της ζημιάς ή γρατζουνιάς...',
      uploadMedia: 'Ανέβασμα Φωτογραφίας ή Βίντεο (προαιρετικό)',
      maxVideoLength: 'Μέγιστο βίντεο 10-15 δευτερόλεπτα ή φωτογραφία',
      takePhoto: 'Λήψη Φωτογραφίας',
      recordVideo: 'Εγγραφή Βίντεο',
      submitReport: 'Υποβολή Αναφοράς',
      reviewed: 'Ελέγχθηκε',
      resolved: 'Επιλύθηκε',
      markReviewed: 'Σήμανση ως Ελεγμένο',
      markResolved: 'Σήμανση ως Επιλυμένο',
      searchPlaceholder: 'Αναζήτηση αναφορών ζημιών...',
      totalEntries: 'συνολικές αναφορές',
      noReports: 'Δεν βρέθηκαν αναφορές ζημιών',
      filterByStatus: 'Φιλτράρισμα με Κατάσταση',
      filterByVehicle: 'Φιλτράρισμα με Όχημα',
      dateReported: 'Ημερομηνία Αναφοράς',
      media: 'Πολυμέσα',
    },
    
    // History
    history: {
      title: 'Ιστορικό Στόλου',
      subtitle: 'Παρακολουθήστε τη χρήση οχημάτων και οδηγών στο χρόνο',
      period: 'Περίοδος',
      last7Days: 'Τελευταίες 7 ημέρες',
      last14Days: 'Τελευταίες 14 ημέρες',
      last30Days: 'Τελευταίες 30 ημέρες',
      mostActiveVehicles: 'Πιο Ενεργά Οχήματα',
      mostActiveDrivers: 'Πιο Ενεργοί Οδηγοί',
      dailyActivity: 'Ημερήσια Δραστηριότητα',
      checkIns: 'Έλεγχοι',
      assignments: 'Αναθέσεις',
      noActivity: 'Δεν υπάρχει δραστηριότητα αυτή την περίοδο',
    },
    
    // Analytics
    analytics: {
      title: 'Στατιστικά',
      vehicleAnalytics: 'Στατιστικά Οχημάτων',
      driverAnalytics: 'Στατιστικά Οδηγών',
      totalKmDriven: 'Συνολικά ΧΛΜ',
      avgKmPerVehicle: 'Μέσος όρος ΧΛΜ/Όχημα',
      avgKmPerCheckIn: 'Μέσος όρος ΧΛΜ/Έλεγχο',
      predictedKmYearEnd: 'Πρόβλεψη ΧΛΜ (Τέλος Έτους)',
      kmDrivenByVehicle: 'ΧΛΜ ανά Όχημα',
      predictedKm: 'Προβλεπόμενα ΧΛΜ',
      vehicleDetails: 'Στοιχεία Οχήματος',
      driverDetails: 'Στοιχεία Οδηγού',
      exportCsv: 'Εξαγωγή CSV',
      filterByVehicle: 'Φιλτράρισμα με Όχημα',
      allVehicles: 'Όλα τα Οχήματα',
      clearFilter: 'Καθαρισμός Φίλτρου',
      checkInCount: 'Αριθμός Ελέγχων',
      lastCheckIn: 'Τελευταίος Έλεγχος',
      avgDaily: 'Μέσος Ημερήσιος',
      kmDrivenByDriver: 'ΧΛΜ ανά Οδηγό',
      complianceRate: 'Ποσοστό Συμμόρφωσης Ελέγχων',
      weeklyPattern: 'Εβδομαδιαίο Μοτίβο Δραστηριότητας (Μέσος Όρος Στόλου)',
      vehiclesUsed: 'Οχήματα που Χρησιμοποιήθηκαν',
      activeDays: 'Ενεργές Ημέρες',
      compliance: 'Συμμόρφωση',
    },
    
    // User Management
    users: {
      title: 'Διαχείριση Χρηστών',
      addUser: 'Προσθήκη Χρήστη',
      editUser: 'Επεξεργασία Χρήστη',
      role: 'Ρόλος',
      admin: 'Διαχειριστής',
      fleetManager: 'Διευθυντής Στόλου',
      workshop: 'Συνεργείο',
      driver: 'Οδηγός',
      superAdmin: 'Υπερδιαχειριστής',
      created: 'Δημιουργήθηκε',
      searchPlaceholder: 'Αναζήτηση χρηστών...',
      totalEntries: 'συνολικοί χρήστες',
      noUsers: 'Δεν βρέθηκαν χρήστες',
      resetPassword: 'Επαναφορά Κωδικού',
      newPassword: 'Νέος Κωδικός',
      passwordResetSuccess: 'Ο κωδικός επαναφέρθηκε επιτυχώς',
    },
    
    // Super Admin
    superAdmin: {
      title: 'Υπερδιαχειριστής',
      platformOverview: 'Επισκόπηση Πλατφόρμας',
      companyManagement: 'Διαχείριση Εταιρειών',
      allUsers: 'Όλοι οι Χρήστες',
      totalCompanies: 'Συνολικές Εταιρείες',
      activeCompanies: 'Ενεργές Εταιρείες',
      totalUsers: 'Συνολικοί Χρήστες',
      totalVehicles: 'Συνολικά Οχήματα',
      totalDrivers: 'Συνολικοί Οδηγοί',
      companiesByPlan: 'Εταιρείες ανά Πλάνο',
      recentSignups: 'Πρόσφατες Εγγραφές',
      quickActions: 'Γρήγορες Ενέργειες',
      viewCompanies: 'Προβολή Εταιρειών',
      manageUsers: 'Διαχείριση Χρηστών',
      platformAnalytics: 'Στατιστικά Πλατφόρμας',
      billingOverview: 'Επισκόπηση Χρεώσεων',
      plan: 'Πλάνο',
      trialEnds: 'Λήξη Δοκιμής',
      users: 'Χρήστες',
      deactivate: 'Απενεργοποίηση',
      activate: 'Ενεργοποίηση',
      extendTrial: 'Παράταση Δοκιμής',
      deleteCompany: 'Διαγραφή Εταιρείας',
      deleteConfirm: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την εταιρεία; Θα διαγραφούν ΟΛΑ τα δεδομένα συμπεριλαμβανομένων χρηστών, οχημάτων και εγγραφών. Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
      loginAs: 'Σύνδεση ως',
      impersonate: 'Υποδυθείτε Χρήστη',
      viewDetails: 'Προβολή Στοιχείων',
      editCompany: 'Επεξεργασία Εταιρείας',
      trialEndDate: 'Ημερομηνία Λήξης Δοκιμής',
      company: 'Εταιρεία',
      searchCompanies: 'Αναζήτηση εταιρειών...',
      searchUsers: 'Αναζήτηση με όνομα, email ή εταιρεία...',
      allPlans: 'Όλα τα Πλάνα',
      allStatus: 'Όλες οι Καταστάσεις',
      allRoles: 'Όλοι οι Ρόλοι',
    },
    
    // Settings
    settings: {
      title: 'Ρυθμίσεις',
      profile: 'Προφίλ',
      account: 'Λογαριασμός',
      notifications: 'Ειδοποιήσεις',
      language: 'Γλώσσα',
      theme: 'Θέμα',
      changePassword: 'Αλλαγή Κωδικού',
      currentPassword: 'Τρέχων Κωδικός',
      newPassword: 'Νέος Κωδικός',
      saveChanges: 'Αποθήκευση Αλλαγών',
    },
    
    // Check-in
    checkIn: {
      title: 'Ημερήσιος Έλεγχος',
      selectVehicle: 'Επιλέξτε Όχημα',
      enterOdometer: 'Εισάγετε Ένδειξη Χιλιομετρητή',
      takePhotos: 'Λήψη Φωτογραφιών',
      exteriorFront: 'Εξωτερικό Μπροστά',
      exteriorBack: 'Εξωτερικό Πίσω',
      exteriorLeft: 'Εξωτερικό Αριστερά',
      exteriorRight: 'Εξωτερικό Δεξιά',
      interiorFront: 'Εσωτερικό Μπροστά',
      interiorBack: 'Εσωτερικό Πίσω',
      submitCheckIn: 'Υποβολή Ελέγχου',
      photosRequired: 'Απαιτούνται 6 φωτογραφίες',
      photosTaken: 'φωτογραφίες ελήφθησαν',
      checkInSuccess: 'Ο έλεγχος υποβλήθηκε επιτυχώς!',
      callAssistance: 'Κλήση Βοήθειας',
      reportDamage: 'Αναφορά Ζημιάς',
    },
    
    // Messages
    messages: {
      success: 'Επιτυχία',
      error: 'Σφάλμα',
      warning: 'Προειδοποίηση',
      info: 'Πληροφορία',
      saved: 'Αποθηκεύτηκε επιτυχώς',
      deleted: 'Διαγράφηκε επιτυχώς',
      updated: 'Ενημερώθηκε επιτυχώς',
      created: 'Δημιουργήθηκε επιτυχώς',
      loginSuccess: 'Επιτυχής σύνδεση',
      loginError: 'Μη έγκυρα διαπιστευτήρια',
      logoutSuccess: 'Αποσυνδεθήκατε επιτυχώς',
      sessionExpired: 'Η συνεδρία έληξε. Παρακαλώ συνδεθείτε ξανά.',
      networkError: 'Σφάλμα δικτύου. Παρακαλώ δοκιμάστε ξανά.',
      serverError: 'Σφάλμα διακομιστή. Παρακαλώ δοκιμάστε αργότερα.',
      confirmDelete: 'Είστε σίγουροι ότι θέλετε να το διαγράψετε;',
      noPermission: 'Δεν έχετε δικαίωμα να εκτελέσετε αυτή την ενέργεια.',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('fleetility_language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('fleetility_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations.en;
        for (const k of keys) {
          if (value && value[k]) {
            value = value[k];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'el' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
